/**
 * Imperative confirm dialog hook.
 *
 * Replaces `window.confirm()` and `confirm()` everywhere in the app —
 * those are a CLAUDE.md hard rule violation (broken UX, no styling, not
 * mockable, blocks the event loop).
 *
 * Two pieces:
 *
 *   1. `<ConfirmProvider>` — mount once near the app root (after the toast
 *      provider). Owns the open/close state of a single shared
 *      `ConfirmDialog` and renders it imperatively.
 *
 *   2. `useConfirm()` — hook that returns an async function:
 *      ```ts
 *      const confirm = useConfirm();
 *      const ok = await confirm({ title: '…', message: '…' });
 *      if (!ok) return;
 *      // proceed
 *      ```
 *
 * The dialog reuses the existing `ConfirmDialog` component so styling,
 * dark mode and keyboard handling stay consistent.
 */
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import ConfirmDialog from '../components/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Label for the confirm button. Default: "Confirmar". */
  confirmLabel?: string;
  /** Confirm button colour. Default: "danger". Use "primary" for safe actions like restore. */
  variant?: 'primary' | 'danger' | 'warning';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<
    | (ConfirmOptions & { isOpen: boolean })
    | null
  >(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((value: boolean) => {
    setState((prev) => (prev ? { ...prev, isOpen: false } : prev));
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ ...options, isOpen: true });
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state ? (
        <ConfirmDialog
          isOpen={state.isOpen}
          onClose={() => close(false)}
          onConfirm={() => close(true)}
          title={state.title}
          message={state.message}
          confirmLabel={state.confirmLabel}
          variant={state.variant}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error(
      'useConfirm() must be used inside <ConfirmProvider> — mount it once near the app root.',
    );
  }
  return ctx;
}

/**
 * Non-throwing variant of {@link useConfirm}. Returns `null` when no
 * `<ConfirmProvider>` is mounted instead of throwing. Used by `ModalShell`,
 * which must render even in trees without a confirm provider.
 */
export function useConfirmOptional(): ConfirmFn | null {
  return useContext(ConfirmContext);
}
